import * as React from "react";
import { CalendarIcon, ChevronDownIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/src/js/lib/utils";
import { DROPDOWN_TRIGGER_CLASS } from "../../ui/dropdown-select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Period,
  RollingPeriod,
  createCustomPeriod,
  getAutoMatchedComparison,
  toCalendarDate,
} from "../periods";
import { ActionButtons } from "./timePeriod/ActionButtons";
import { ComparisonSection } from "./timePeriod/ComparisonSection";
import { CustomRangeFooter } from "./timePeriod/CustomRangeFooter";
import { CustomRangeView } from "./timePeriod/CustomRangeView";
import { FocusSummary } from "./timePeriod/FocusSummary";
import { PresetsList } from "./timePeriod/PresetsList";
import { DraftRange, isDraftRangeComplete } from "./timePeriod/draftRange";
import { getComparisonChoices } from "./timePeriod/periodChoices";

interface TimePeriodSelectProps {
  // Every reporting year, newest first
  reportingYears: Period[];
  rollingPeriods: RollingPeriod[];
  focusPeriod: Period;
  comparisonPeriod: Period;
  // The group's, which decides where a custom range's days start and end
  timeZone: string;
  onChange: (periods: { focusPeriod: Period; comparisonPeriod: Period }) => void;
}

interface Selection {
  focusPeriod: Period;
  comparisonPeriod: Period;
  autoMatchComparison: boolean;
}

const CUSTOM_FOCUS_KEY = "custom-focus";

// One place to pick the periods the whole dashboard shows, applied together with Apply. The presets
// are the newest reporting years, some rolling periods and a custom range of days chosen on a
// calendar. The comparison follows the focus until someone picks another from what fits it
export function TimePeriodSelect({
  reportingYears,
  rollingPeriods,
  focusPeriod,
  comparisonPeriod,
  timeZone,
  onChange,
}: TimePeriodSelectProps) {
  const toggleId = React.useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [autoMatchComparison, setAutoMatchComparison] = React.useState(true);
  const [selection, setSelection] = React.useState<Selection>({
    focusPeriod,
    comparisonPeriod,
    autoMatchComparison,
  });
  // Whether the calendar shows, instead of the focus summary, for choosing a custom range
  const [isCalendarShown, setIsCalendarShown] = React.useState(false);
  // The days clicked on the calendar so far. It starts empty, and the focus only becomes the custom
  // range once both days are chosen
  const [draftRange, setDraftRange] = React.useState<DraftRange>({});

  const isRangeComplete = isDraftRangeComplete(draftRange);

  const getAutoMatched = (period: Period) => getAutoMatchedComparison(period, reportingYears);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSelection({
        focusPeriod,
        // The supplied comparison can predate auto-match, e.g. the focus itself when nothing is older
        comparisonPeriod: autoMatchComparison ? getAutoMatched(focusPeriod) : comparisonPeriod,
        autoMatchComparison,
      });
      const isFocusCustom = focusPeriod.key === CUSTOM_FOCUS_KEY;
      setIsCalendarShown(isFocusCustom);
      setDraftRange(
        isFocusCustom ? { firstDay: focusPeriod.firstDay, lastDay: focusPeriod.lastDay } : {},
      );
    }
    setIsOpen(open);
  };

  // A comparison someone picked stays only if it is still one of the choices for the new focus
  // (taken from the new choices, since a custom focus's earlier years change with its dates).
  // Otherwise the comparison goes back to following the focus
  const selectFocus = (period: Period) => {
    setSelection((previous) => {
      const pickedComparison = previous.autoMatchComparison
        ? undefined
        : getComparisonChoices(period, reportingYears, timeZone).find(
            (choice) => choice.key === previous.comparisonPeriod.key,
          );
      return {
        ...previous,
        focusPeriod: period,
        comparisonPeriod: pickedComparison ?? getAutoMatched(period),
        autoMatchComparison: !pickedComparison,
      };
    });
  };

  // Picking a preset shows the focus summary again, not the calendar
  const selectPreset = (period: Period) => {
    selectFocus(period);
    setIsCalendarShown(false);
    setDraftRange({});
  };

  const selectComparison = (period: Period) => {
    setSelection((previous) => ({
      ...previous,
      comparisonPeriod: period,
      autoMatchComparison: false,
    }));
  };

  // A custom range starts with nothing chosen, so there is no range to undo: pick the first day,
  // then the last. The focus stays as it was until then
  const startCustomFocus = () => {
    setIsCalendarShown(true);
    setDraftRange({});
  };

  const changeAutoMatch = (checked: boolean) => {
    setSelection((previous) => ({
      ...previous,
      autoMatchComparison: checked,
      comparisonPeriod: checked ? getAutoMatched(previous.focusPeriod) : previous.comparisonPeriod,
    }));
  };

  // The first click sets the first day, the second the last (a second click on the same day is a
  // one-day range), and a click after that starts over. Clicking a one-day range again clears it
  const selectRange = (range: DateRange | undefined) => {
    if (!range?.from) {
      setDraftRange({});
      return;
    }
    const firstDay = toCalendarDate(range.from);
    if (!range.to) {
      setDraftRange({ firstDay });
      return;
    }
    const lastDay = toCalendarDate(range.to);
    setDraftRange({ firstDay, lastDay });
    selectFocus(createCustomPeriod(CUSTOM_FOCUS_KEY, firstDay, lastDay, timeZone));
  };

  const apply = () => {
    setAutoMatchComparison(selection.autoMatchComparison);
    onChange({
      focusPeriod: selection.focusPeriod,
      comparisonPeriod: selection.comparisonPeriod,
    });
    setIsOpen(false);
  };

  const comparisonProps = {
    focusPeriod: selection.focusPeriod,
    comparisonPeriod: selection.comparisonPeriod,
    autoMatchComparison: selection.autoMatchComparison,
    reportingYears,
    timeZone,
    onSelectComparison: selectComparison,
    onChangeAutoMatch: changeAutoMatch,
  };

  const actionButtonProps = {
    isApplyDisabled: isCalendarShown && !isRangeComplete,
    onCancel: () => setIsOpen(false),
    onApply: apply,
  };

  return (
    <div className="tw:grid tw:gap-2">
      {/* Clicking a label clicks its control, but the other filters' titles do nothing when clicked */}
      <label
        htmlFor={toggleId}
        className="tw:text-sm tw:font-medium tw:leading-none"
        onClick={(event) => event.preventDefault()}
      >
        Time Period
      </label>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          id={toggleId}
          aria-label={`Time Period: ${focusPeriod.label} vs ${comparisonPeriod.label}`}
          className={cn(DROPDOWN_TRIGGER_CLASS, "tw:w-[420px] tw:max-w-full tw:cursor-pointer")}
        >
          <span className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
            <CalendarIcon className="tw:size-4 tw:text-neutral-500" aria-hidden="true" />
            <span className="tw:truncate">
              <span className="tw:font-semibold tw:text-[#17436d]">{focusPeriod.label}</span>
              <span className="tw:text-neutral-500"> vs {comparisonPeriod.label}</span>
            </span>
          </span>
          <ChevronDownIcon className="tw:size-4 tw:opacity-50" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={16}
          aria-label="Time Period"
          className={cn(
            "tw:flex tw:max-h-(--radix-popover-content-available-height) tw:max-w-[calc(100vw-2rem)] tw:flex-col tw:p-0 tw:text-sm",
            isCalendarShown ? "tw:w-[920px]" : "tw:w-[680px]",
          )}
          // The dropdowns open in their own layer outside this panel, and picking from them isn't an outside press
          onInteractOutside={(event) => {
            if ((event.target as HTMLElement).closest('[role="menu"]')) event.preventDefault();
          }}
        >
          {/* The presets sit above the rest on a phone and beside it from the sm breakpoint up */}
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:sm:flex-row">
            <PresetsList
              reportingYears={reportingYears}
              rollingPeriods={rollingPeriods}
              focusPeriod={selection.focusPeriod}
              isCalendarShown={isCalendarShown}
              onSelectPreset={selectPreset}
              onStartCustomRange={startCustomFocus}
            />

            {isCalendarShown ? (
              <CustomRangeView
                draftRange={draftRange}
                reportingYears={reportingYears}
                timeZone={timeZone}
                onSelectRange={selectRange}
              >
                <ComparisonSection {...comparisonProps} />
              </CustomRangeView>
            ) : (
              <div className="tw:grid tw:min-w-0 tw:flex-1 tw:content-start tw:gap-5 tw:p-4">
                <FocusSummary title="Focus" period={selection.focusPeriod} />
                <ComparisonSection {...comparisonProps} />
                <div className="tw:border-t tw:border-neutral-200 tw:pt-4">
                  <ActionButtons {...actionButtonProps} />
                </div>
              </div>
            )}
          </div>
          {isCalendarShown && (
            // Outside the scrolling area so Apply stays in view however tall the calendar view is
            <CustomRangeFooter
              isRangeComplete={isRangeComplete}
              focusPeriod={selection.focusPeriod}
              comparisonPeriod={selection.comparisonPeriod}
            >
              <ActionButtons {...actionButtonProps} />
            </CustomRangeFooter>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
